import {
    Button,
    DatePicker,
    Dropdown,
    Form,
    Input,
    List,
    message,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from "antd";
import {
    CloseOutlined,
    DownloadOutlined,
    DownOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    EyeOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    MailOutlined,
    MinusOutlined,
    OrderedListOutlined,
    PhoneOutlined,
    RedoOutlined,
    SaveFilled,
    UpOutlined
} from "@ant-design/icons";
import "./PracticeDetailPanel.css"
import React, {useEffect, useRef, useState} from "react";
import {UtilsService} from "../../services/Utils.service";
import "./UserDetails.css";
import {
    CallRegistryDTO,
    FileTsAuditDto,
    Installment,
    PAYMENT_TYPE_LABEL,
    PaymentType,
    Person,
    ProcessedPracticeDetail,
    readableToEnumStatus,
    SendAttachment,
    SendData,
    SendStatusLabel,
    StatusCodeE,
    SUSPENSION_REASON_LABELS
} from "../../model/models";
import {HttpService} from "../../services/http/Http.service";
import {TemplateUtils} from "../../services/utils/TemplateUtils";
import type {Dayjs} from "dayjs";
import dayjs from "dayjs";
import SendModulesModal from "./operationModals/SendModulesModal";
import InstallmentsModal from "./operationModals/InstallmentsModal";
import HeirModal from "./operationModals/HeirModal";
import ArchiveModal from "./operationModals/ArchiveModal";
import {HttpErrorService} from "../../services/http/HttpError.service";
import ConfirmModal from "../../components/confirmModal/ConfirmModal";
import PracticeDetailPanelSteps from "./PracticeDetailPanelSteps";
import Recalculation from "./operationModals/Recalculation";
import PaymentConfirmedModal from "./operationModals/PaymentConfirmedModal";
import SuspendModal from "./operationModals/SuspendModal";
import MissedPaymentModal from "./operationModals/MissedPaymentModal";
import type {Rule} from "antd/es/form";
import {SendUtils} from "../../services/utils/SendUtils";
import {AuthService} from "../../services/Auth.service";

const {Title} = Typography;

interface DetailPanelProps {
    onBack?: () => void,
    simulatorId?: number | undefined,
    setPerson?: (value: (((prevState: (Person | undefined)) => (Person | undefined)) | Person | undefined)) => void,
    person: Person,
    practice: ProcessedPracticeDetail,
    refreshPractices?: () => void,
    refreshPerson?: () => Promise<void>,
    refreshDocuments?: () => void | undefined,
    onPaidInstallmentsChange?: (hasPaid: boolean) => void;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: 'select' | 'date' | 'text';
    record: Installment;
    index: number;
    children: React.ReactNode;
    installmentForm: any;
}

const EditableCell: React.FC<EditableCellProps> = ({
                                                       editing,
                                                       dataIndex,
                                                       title,
                                                       inputType,
                                                       record,
                                                       index,
                                                       children,
                                                       installmentForm,
                                                       ...restProps
                                                   }) => {
    let inputNode;

    if (inputType === 'date') {
        inputNode = (
            <DatePicker format="DD/MM/YYYY" className={"custom-datepicker"}
                        value={record.dateOfPayment ? dayjs(record.dateOfPayment) : null}/>
        );
    }else if (inputType === 'select') {
        inputNode = (
            <Select
                options={[
                    { value: PaymentType.PAGOPA, label: "PagoPA" },
                    { value: PaymentType.BONIFICO_BANCARIO, label: "Bonifico Bancario" },
                ]}
                allowClear
                placeholder="Seleziona"
                dropdownStyle={{minWidth: 200}}
            />
        );
    }

    const rules: Rule[] =
        dataIndex === "dateOfPayment"
            ? [
                {
                    validator: async (_: unknown, value: Dayjs | null) => {
                        const v = installmentForm.getFieldsValue();
                        if ((v.dateOfPayment && !value) || (value && !v.dateOfPayment)) {
                            return Promise.reject(
                                new Error("Data  pagamento e obbligatoro ")
                            );
                        }
                        return Promise.resolve();
                    },
                },
            ]
            : dataIndex === "paymentType"
            ? [
                {
                    validator: async (_: unknown, value?: PaymentType) => {
                        const v = installmentForm.getFieldsValue();
                        if ((v.paymentType && !value) || (value && !v.paymentType)) {
                            return Promise.reject(
                                new Error(" tipo pagamento e obbligatoro insieme")
                            );
                        }
                        return Promise.resolve();
                    },
                },
            ]
            : [];

    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item name={dataIndex} className="no-margin" rules={rules}>
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};


export default function PracticeDetailPanel({
                                                onBack,
                                                simulatorId,
                                                setPerson,
                                                person,
                                                practice,
                                                refreshPractices,
                                                refreshPerson,
                                                refreshDocuments,
                                                onPaidInstallmentsChange,
                                            }: Readonly<DetailPanelProps>) {
    const [form] = Form.useForm();
    const [installmentForm] = Form.useForm();
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);

    const [isCall, setIsCall] = useState(false);
    const [updateContactModal, setUpdateContactsModal] = useState(false)

    const [changeActivityModal, setChangeActivityModal] = useState(false)
    const [isChangingActivity, setIsChangingActivity] = useState(false)
    const [callId, setCallId] = useState<number>(0)
    const [isSubmittingCall, setIsSubmittingCall] = useState(false)
    const [auditTrail, setAuditTrail] = useState<FileTsAuditDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [listOfInstallments, setListOfInstallments] = useState<Installment[]>([])

    const [hasAnyInstallmentsPaid, setHasAnyInstallmentsPaid] = useState<boolean>(false)
    const hasInstallments = listOfInstallments.length > 1

    const [eredeRicalcoloInfo, setEredeRicalcoloInfo] = useState<{
        isErede: boolean;
        eligible: boolean;
    } | null>(null);

    const fetchListOfInstallments = async () => {
        try {
            const data: Installment[] = (
                await HttpService.getInstanceAxios().get(`/installment/simulator/${simulatorId}/person/${person?.id}`)
            ).data;

            const anyPaid = data.some(i => i.dateOfPayment != null);
            onPaidInstallmentsChange?.(anyPaid);
            if (anyPaid) setHasAnyInstallmentsPaid(true)

            if (!data || data.length === 0) {
                setListOfInstallments([]);
                return;
            }

            const reordered = filterInstallments(data);
            setListOfInstallments(reordered);
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero delle rate", error, true);
        }
    };

    useEffect(() => {
        if (!person?.id || !simulatorId) return;
        HttpService.getInstanceAxios()
            .get(`/succession/successor/${person.id}/ricalcolo-eligibility?simulatorId=${simulatorId}`)
            .then(res => setEredeRicalcoloInfo(res.data))
            .catch(() => setEredeRicalcoloInfo(null));
    }, [person?.id, simulatorId]);

    const exportAuditExcel = async () => {
        if (simulatorId == null || person?.id == null) {
            message.error("Simulator/Persona non valido.");
            return;
        }

        try {
            const res = await HttpService.getInstanceAxios().get(
                `/file-ts-audit/simulator/${simulatorId}/person/${person.id}/export`,
                { responseType: "blob" }
            );

            const fullname = (person.lastName + "_" + person.firstName).toUpperCase().replace(/\s+/g, "_");
            const filename = `${fullname}_dati_storici.xlsx`;

            const blob = new Blob(
                [res.data],
                { type: res.headers?.["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
            );

            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename; document.body.appendChild(a);
            a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (error) {
            HttpErrorService.handle("Errore durante l'esportazione dell'Excel", error, true);
        }
    };


    /** USE CASES — quando mostrare la riga del pagamento unico (PU):
     - solo PU (nessuna rata) => PU
     - rate a monte, nessuna rata pagata => rate + PU
     - rate a monte, almeno una rata pagata => solo le rate
     - rate a valle (nessun mancato pagamento) => solo le rate
     - mancato pagamento (con zero o più rate pagate) => rate pagate + PU
    **/

    function filterInstallments(data: Installment[]): Installment[] {
        const byVersionThenOrder = (a: Installment, b: Installment) =>
            a.version - b.version || a.order - b.order;

        const uniquePaymentItem = data.find(item => item.uniquePayment);
        const rate = data.filter(item => !item.uniquePayment);
        const paidRate = rate.filter(item => item.dateOfPayment !== null);

        // nessuna rata → c'è solo il pagamento unico
        if (rate.length === 0) {
            return uniquePaymentItem ? [uniquePaymentItem] : [];
        }
        if (!uniquePaymentItem) {
            return [...rate].sort(byVersionThenOrder);
        }

        // mancato pagamento attivo → il PU è stato rigenerato con una versione superiore a TUTTE le rate.
        // Annullandolo (o rigenerando le rate con ricalcolo/nuova rateizzazione) le versioni si riallineano.
        const maxRateVersion = Math.max(...rate.map(item => item.version));
        const isUniquePaymentNewer = maxRateVersion < uniquePaymentItem.version;

        // mancato pagamento → rate già pagate + nuovo pagamento unico
        if (isUniquePaymentNewer) {
            return [...paidRate, uniquePaymentItem].sort(byVersionThenOrder);
        }

        // rate a valle → il pagamento unico non è più un'opzione
        const isAvalle = rate.every(item => item.avalle);
        // rate a monte con almeno una rata pagata → il pagamento unico non è più un'opzione
        if (isAvalle || paidRate.length > 0) {
            return [...rate].sort(byVersionThenOrder);
        }

        // rate a monte, nessuna rata pagata → si può ancora pagare tutto in una volta
        return [...rate, uniquePaymentItem].sort(byVersionThenOrder);
    }

    const [pendingLastSave, setPendingLastSave] = useState<{
        record: Installment;
        payload: { dateOfPayment: string | null; paymentType: PaymentType | null };
    } | null>(null);


    const isLastInstallmentRow = (rec: Installment) => {
        if (rec.uniquePayment) return true;
        const nonUnique = listOfInstallments.filter(i => !i.uniquePayment);
        return nonUnique.filter(i => i.id !== rec.id).every(i => !!i.dateOfPayment);
    };

    const [saveLoading, setSaveLoading] = useState(false);
    const [isSendDetailsExpanded, setIsSendDetailsExpanded] = useState(false);

    const doSave = async (
        record: Installment,
        payload: { dateOfPayment: string | null; paymentType: PaymentType | null }
    ) => {
        setSaveLoading(true);
        try {
            await HttpService.getInstanceAxios().put(`/installment/${record.id}`, payload);

            setListOfInstallments(prev =>
                prev.map(item =>
                    item.id === record.id
                        ? {...item, dateOfPayment: payload.dateOfPayment ?? item.dateOfPayment,
                            paymentType: payload.paymentType ?? item.paymentType ?? null,
                        }
                        : item
                )
            );

            setEditingKey(null);
            await fetchListOfInstallments();
            message.success("Data di pagamento aggiornata!");
        } catch (error) {
            HttpErrorService.handle("Errore durante il salvataggio della rata pagata", error, true);
        } finally {
            setSaveLoading(false);
        }
    };


    const [listOfSend, setListOfSend] = useState<SendData[]>([])
    const fetchListOfSend = async () => {
        try {
            const data: SendData[] = (
                await HttpService.getInstanceAxios().get(`/send/info/${simulatorId}/${person?.id}`)
            ).data;

            if (!data || data.length === 0) {
                setListOfSend([]);
                return;
            }

            //const reordered = data.sort((a, b) => a.createdAt - b.createdAt);

            setListOfSend(data);
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero dei documenti di SEND", error, true);
        }
    };

    useEffect(() => {
        if (!simulatorId || !person?.id) return;

        let cancelled = false;

        (async () => {
            await loadAuditTrail();
            if (cancelled) return;

            await fetchListOfInstallments();
            if (cancelled) return;

            await fetchListOfSend();
        })();

        return () => {
            cancelled = true;
        };
    }, [simulatorId, person?.id]);

    const isStatusFirstRender = useRef(true);
    useEffect(() => {
        if (isStatusFirstRender.current) {
            isStatusFirstRender.current = false;
            return;
        }
        if (!simulatorId || !person?.id) return;
        (async () => {
            await loadAuditTrail();
            await fetchListOfInstallments();
        })();
    }, [practice.status]);

    const isArchived = readableToEnumStatus[practice.status] === StatusCodeE.RENDICONTATE

    const loadAuditTrail = async (): Promise<FileTsAuditDto[]> => {
        try {
            const response = await HttpService.getInstanceAxios().get(`/file-ts-audit/simulator/${simulatorId}/person/${person?.id}`);
            setAuditTrail(response.data);
            return response.data;
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero dello storico stato", error, true);
            return [];
        }
    };

    const fetchAuditTrail = async (): Promise<void> => {
        await loadAuditTrail();
    };

    const columns = [
        {
            title: "Protocollo archiviazione",
            dataIndex: "protocolNumber",
            key: "protocolNumber",
        },
        {
            title: "Codice esenzione",
            dataIndex: "exemptionCodes",
            key: "exemptionCodes",
            render: () => practice.exemptionCodes.join(" - ")
        },
        {
            title: "Anno",
            dataIndex: "years",
            key: "years",
            render: () => practice.years.join(" - ")
        },
        {
            title: "Importo ticket",
            dataIndex: "totalTickets",
            key: "totalTickets",
            render: () => "€ " + UtilsService.formatNumber(practice.totalTickets)
        },
        {
            title: "Sanzione",
            dataIndex: "totalSanction",
            key: "totalSanction",
            render: () => "€ " + UtilsService.formatNumber(practice.totalSanction)
        },
        {
            title: "Spese amministrative",
            dataIndex: "totalExpense",
            key: "totalExpense",
            render: () => "€ " + UtilsService.formatNumber(practice.totalExpense)
        },
        {
            title: "Interessi",
            dataIndex: "totalInterest",
            key: "totalInterest",
            render: () => "€ " + UtilsService.formatNumber(practice.totalInterest)
        },
        {
            title: "Importo totale",
            dataIndex: "total",
            key: "total",
            render: () => "€ " + UtilsService.formatNumber(practice.total)
        }
    ];

    const [editingKey, setEditingKey] = useState<number | null>(null);

    const isEditing = (record: Installment) => record.id === editingKey;

    const edit = (record: Installment) => {
        installmentForm.setFieldsValue({
            ...record,
            dateOfPayment: record.dateOfPayment ? dayjs(record.dateOfPayment) : null,
            paid: !!record.dateOfPayment,
        });
        setEditingKey(record.id);
    };

    const save = async (record: Installment) => {
        try {
            const row = await installmentForm.validateFields();

            const payload = {
                dateOfPayment: row.dateOfPayment?.toISOString() ?? null,
                paymentType: row.paymentType ?? null,
            };

            if (payload.dateOfPayment !=null && isLastInstallmentRow(record)) {

                setPendingLastSave({ record, payload });
                return;
            }


            await doSave(record, payload);
        } catch (error) {
            HttpErrorService.handle("Errore durante il salvataggio della rata pagata", error, true);
        }
    };


    const cancel = () => {
        setEditingKey(null);
    };

    const isAttesaPagamento = readableToEnumStatus[practice.status] === StatusCodeE.ATTESA_PAGAMENTO;

    const canEditInstallment = (r: Installment) => isAttesaPagamento && !(r.dateOfPayment && !r.manualPayment);

    const columnsRate = [
        {
            title: "Rata",
            key: "order",
            editable: false,
            render: (record: Installment) => record.uniquePayment ? "Rata unica" : record.order
        },
        {
            title: "Importo",
            key: "total",
            editable: false,
            render: (record: Installment, _: any) => {
                return `€ ${UtilsService.formatNumber(record.total)}`;
            }
        },
        {
            title: "Scadenza",
            key: "deadline",
            editable: false,
            render: ((record: Installment) => record.deadline !== null ? UtilsService.formatDateString(record.deadline) : "-")
        },
        {
            title: "Pagata",
            key: "paid",
            editable: false,
            render: ((record: Installment) => record.dateOfPayment ? "Si" : "No")
        },
        {
            title: "Data di pagamento",
            key: "dateOfPayment",
            editable: true,
            render: (record: Installment) => record?.dateOfPayment ? dayjs(record.dateOfPayment).format("DD/MM/YYYY") : "-"

        },
        {
            title: "Tipo pagamento",
            key: "paymentType",
            editable: true,
            render: (record: Installment) => {
                return (
                    <span>
                          {record.paymentType ? (PAYMENT_TYPE_LABEL[record.paymentType] ?? record.paymentType) : "-"}
                        {record.manualPayment && record.dateOfPayment && (
                            <Tooltip title="Pagamento manuale">
                                <InfoCircleOutlined style={{color: "#faad14", marginLeft: 6, fontSize: 12, verticalAlign: "text-bottom"}}/>
                            </Tooltip>
                        )}
                   </span>
                );
            },
        },
        {
            title: 'Azioni',
            key: 'action',
            render: (_: any, record: Installment) => {
                const editable = isEditing(record);
                const canEdit = canEditInstallment(record);
                return editable ? (
                    <span style={{ display: 'inline-flex', gap: 8 }}>
        <Button
            icon={<SaveFilled />}
            onClick={() => save(record)}
            loading={saveLoading}
        />
        <Button
            icon={<CloseOutlined />}
            onClick={cancel}
        />
      </span>
                ) : (
                    <Button
                        icon={<EditOutlined />}
                        disabled={editingKey !== null || !canEdit}
                        onClick={() => edit(record)}
                    />
                );
            },
        }

    ];

    const mergedColumns = columnsRate.map(col => {
        if (!col.editable) return col;
        return {
            ...col,
            onCell: (record: Installment) => ({
                record,
                inputType: col.key === 'dateOfPayment' ? 'date' : 'select',
                dataIndex: col.key,
                title: col.title,
                editing: isEditing(record),
                installmentForm
            }),
        };
    });

    const columnsSend = [

        {
            title: "IUN",
            key: "iun",
            editable: false,
            render: (record: SendData) =>
                record.iun!=null ?<Typography.Text copyable={true} strong={true}>{ record.iun }</Typography.Text> : <MinusOutlined/>
        },
        {
            title: "Data invio",
            key: "createdAt",
            editable: false,
            render: ((record: SendData) => record.createdAt !== null ? UtilsService.formatDateString(record.createdAt) : "-")
        },
        {
            title: "Ultimo aggiornamento",
            key: "updatedAt",
            editable: false,
            render: ((record: SendData) => record.updatedAt !== null ? UtilsService.formatDateHour(record.updatedAt) : "-")
        },
        {
            title: "Stato",
            key: "status",
            editable: false,
            render: (record: SendData) =>
                ( record.status ? (

                    <Typography.Text type={SendUtils.getStatusColor(record.status)}>{SendStatusLabel[record.status]}</Typography.Text>

                ) : <MinusOutlined/> )
        },
        {
            title: (
                <div
                    onClick={() => setIsSendDetailsExpanded(!isSendDetailsExpanded)}
                    className={"expand-icon column-toggle"}
                >
                    Dettagli
                    {isSendDetailsExpanded ? (
                        <UpOutlined className={"expand-icon-size"}/>
                    ) : (
                        <DownOutlined className={"expand-icon-size"}/>
                    )}
                </div>
            ),
            key: "details",
            editable: false,
            render: (record: SendData) =>{
                if (!isSendDetailsExpanded) {
                    return <MinusOutlined/>;
                }
                const rows = [];
                if(record.serviceLevel!=null){
                    rows.push(<Tag color={"default"} >{record.serviceLevel}</Tag>);
                    rows.push(<br/>);
                }
                if(record.physicalAddress!=null){
                    rows.push(
                        <Typography.Text type={"secondary"} > {record.physicalAddress.address} {record.physicalAddress.addressDetails} <br/>
                            {record.physicalAddress.zip} {record.physicalAddress.municipality} ({record.physicalAddress.province})</Typography.Text>
               );}

                if(record.notificationDate!=null){
                    rows.push(<br/>);
                    rows.push(<Tag color={"success"} >{UtilsService.formatDateString(record.notificationDate)}</Tag>);
                }

                for (let i = 0; i < record.errors.length ; i++) {
                    // note: we are adding a key prop here to allow react to uniquely identify each
                    // element in this array. see: https://reactjs.org/docs/lists-and-keys.html
                    rows.push(
                        <Tooltip title={record.errors[i].description} color={"red"}>
                        <Typography.Text type={"danger"}>{SendUtils.getErrorCodeDecoded(record.errors[i].code)}</Typography.Text>
                    </Tooltip>);
                }
                return <div>{rows}</div>;

            }
        },
        {
            title: 'Allegati',
            key: 'attachments',
            dataIndex: "attachments",
            render: (_:any, data: SendData) => (
                <>
                    <div className={"send-attachment-grid"}>
                        {data.attachments.map((attachment:SendAttachment, index:number) => {
                            let color = attachment.title.length > 5 ? 'geekblue' : 'green';
                            return (
                                <React.Fragment key={attachment.key ?? index}>
                                    <div>
                                        { attachment.description!=null ?
                                            <Tooltip title={attachment.title}><Tag color={color} >
                                                {attachment.description.toUpperCase()}
                                            </Tag> </Tooltip>: null
                                        }
                                    </div>
                                    <Tooltip title={`Visualizza ${attachment.description ?? attachment.title}`}>
                                        <Button icon={<EyeOutlined/>} size="middle"
                                                disabled={data.iun == null}
                                                onClick={() => SendUtils.downloadDocument(data.iun != null ? data.iun : 'ND', attachment.key)}/>
                                    </Tooltip>
                                </React.Fragment>
                            );
                        })}
                    </div>
                    {AuthService.isAdmin() &&
                        <Button className="relaod-button" danger={true} title={"Ricarica"}
                                onClick={()=>reloadByIun(data)} disabled={data.iun==null}
                                size={"small"}
                                type={"primary"} icon={<RedoOutlined/>}>
                        </Button>
                    }
                </>
            )
        }

    ];

    const [openModalKey, setOpenModalKey] = useState<string | null>(null);

    const handlePlusMenu = (e: any) => {
        setOpenModalKey(e.key);
    };

    const handleCloseModal = () => {
        setOpenModalKey(null);
    };

    const archiveModal = (
        <ArchiveModal
            onClose={handleCloseModal}
            simulatorId={simulatorId}
            onStatusChange={refreshPractices}
            fetchAuditTrail={fetchAuditTrail}
            person={person}
            practice={practice}
            refreshDocuments={refreshDocuments}
        />
    );

    const modalComponents: { [key: string]: React.ReactNode } = {
        modules: <SendModulesModal visible={true} onClose={handleCloseModal} email={person?.contact?.email ?? ""}/>,
        installments:
            <InstallmentsModal
                visible={true}
                onClose={handleCloseModal}
                simulatorId={simulatorId}
                refreshInstallments={fetchListOfInstallments}
                protocolNumber={practice.protocolNumber}
                protocolDate={practice.createdAt}
                dataNotifica={practice.communicationDate}
                praticeType={practice.type}
                refreshPractices={refreshPractices}
                person={person}
                refreshDocuments={refreshDocuments}
            />,
        heir: <HeirModal
            visible={true}
            onClose={handleCloseModal}
            refreshPractices={refreshPractices}
            simulatorId={simulatorId}
            person={person}
            refreshPerson={refreshPerson}
            installmentsCount={listOfInstallments.length}
            protocolNumber={practice.protocolNumber}
        />,
        recalculation: <Recalculation
            visible={true}
            onClose={handleCloseModal}
            simulatorId={simulatorId}
            personId={person?.id}
            personCF={person?.fiscalCode}
            protocolNumber={practice.protocolNumber}
            protocolDate={practice.createdAt}
            refreshPractices={refreshPractices}
            refreshDocuments={refreshDocuments}
            refreshInstallments={fetchListOfInstallments}
            hasAnyInstallmentsPaid={hasAnyInstallmentsPaid}
            hasInstallments={hasInstallments}
        />,
        paymentConfirmed: <PaymentConfirmedModal
            onClose={handleCloseModal}
            simulatorId={simulatorId}
            personId={person?.id}
            onStatusChange={refreshPractices}
            fetchAuditTrail={fetchAuditTrail}
        />,
        missedPayment: <MissedPaymentModal
            onClose={handleCloseModal}
            simulatorId={simulatorId}
            personId={person?.id}
            onStatusChange={refreshPractices}
            fetchAuditTrail={fetchAuditTrail}
            refreshDocuments={refreshDocuments}
            refreshInstallments={fetchListOfInstallments}
            personCF={person?.fiscalCode}
            protocolNumber={practice.protocolNumber}
        />,
        suspendDirect: <SuspendModal
            onClose={handleCloseModal}
            simulatorId={simulatorId}
            personId={person?.id}
            onStatusChange={refreshPractices}
            fetchAuditTrail={fetchAuditTrail}
        />,
        archive: archiveModal
    };

    const [listOfCalls, setListOfCalls] = useState<CallRegistryDTO[]>([])
    const fetchListOfCalls = async () => {
        try {
            const data = await HttpService.getInstanceAxios().get(`/registry/list/${person?.id}/${simulatorId}`)
            setListOfCalls(data.data.data)
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero delle chiamate", error, true)
        }
    };

    useEffect(() => {
        fetchListOfCalls();
    }, []);

    const [isRateExpanded, setIsRateExpanded] = useState(false);
    const [isCallLogExpanded, setIsCallLogExpanded] = useState(false);
    const [isSendExpanded, setIsSendExpanded] = useState(false);

    const changeActivity = async () => {
        setIsChangingActivity(true)
        try {
            await HttpService.getInstanceAxios().put(`/registry/toggle/${callId}`)
            fetchListOfCalls()
            setChangeActivityModal(false)
        } catch (error) {
            HttpErrorService.handle("Errore durante il passaggio di stato della chiamata", error, true)
        } finally {
            setIsChangingActivity(false)
        }
    }

    const handleSubmit = async (status: true | false) => {
        if (isSubmittingCall) return;
        setIsSubmittingCall(true);
        try {
            const values = await form.validateFields();
            const payload = {
                isDone: status,
                note: values.note,
                metadata: {
                    fullName: values.name,
                    email: values.email,
                    phone: values.phone,
                }
            };
            await HttpService.getInstanceAxios().post(`/registry/register/${person?.id}/${simulatorId}`, payload)
            fetchListOfCalls()
            form.resetFields()
        } catch (error) {
            HttpErrorService.handle("Errore durante il salvataggio della chiamata", error, true)
        } finally {
            setIsSubmittingCall(false);
        }
    };

    const updateContactFromCall = async () => {
        const values = form.getFieldsValue(["name", "phone", "email"]);
        try {
            await HttpService.getInstanceAxios().put(`/contact/${person?.id}`, {
                name: values.name,
                email: values.email,
                phone: values.phone
            });
            if (setPerson && person) {
                setPerson({
                    ...person,
                    contact: {
                        ...person.contact,
                        ...(values.name && {name: values.name}),
                        ...(values.email && {email: values.email}),
                        ...(values.phone && {phone: values.phone})
                    }
                });

                form.setFieldsValue({
                    name: values.name,
                    email: values.email,
                    phone: values.phone
                });
            }

            message.success("Contatti aggiornati con successo!");
            setUpdateContactsModal(false);
        } catch (error) {
            HttpErrorService.handle("Errore durante l'aggiornamento del contatto.", error, true);
        }
    }

    const isSuspended = readableToEnumStatus[practice.status] === StatusCodeE.SOSPESE
    let suspensionReason = "";
    if (isSuspended) {
        const suspendedAudit = auditTrail.filter(entry => entry.status.code === "SOSPESE")
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

        if (suspendedAudit && suspendedAudit.metadata.causaSospensione) {
            suspensionReason = SUSPENSION_REASON_LABELS[suspendedAudit.metadata.causaSospensione] || suspendedAudit.metadata.causaSospensione;
        }
    }
    const isPaid = readableToEnumStatus[practice.status] === StatusCodeE.PAGATE
    const isDaRendicontare = readableToEnumStatus[practice.status] === StatusCodeE.DA_RENDICONTARE
    const isRendicontata = readableToEnumStatus[practice.status] === StatusCodeE.RENDICONTATE
    const isTransferred = readableToEnumStatus[practice.status] === StatusCodeE.TRASFERITE
    const isSigned = readableToEnumStatus[practice.status] === StatusCodeE.NOTIFICA
    const isWaitingForPayment = readableToEnumStatus[practice.status] === StatusCodeE.ATTESA_PAGAMENTO
    const causa = auditTrail?.[0]?.metadata?.causaAttesaPagamento;
    const preInstallments = hasInstallments && !["RATEIZZAZIONE", "CONFERMA_PAGAMENTO"].includes(causa ?? "");
    const postInstallments = hasInstallments && ["RATEIZZAZIONE"].includes(causa ?? "");
    const isDeath = !person.active;
    const hasProtocol = practice.protocolNumber !== null;
    const isNotified = practice.communicationDate !== null;

    const installmentsReasons: { condition: boolean; message: string }[] = [
        {condition: isArchived, message: "La pratica è archiviata"},
        {condition: isSuspended, message: "La pratica è sospesa"},
        {condition: preInstallments, message: "La pratica è già rateizzata a monte"},
        {condition: (postInstallments && (isSigned || isWaitingForPayment)), message: "La pratica è già rateizzata e firmata"},
        {condition: isDeath, message: "La pratica è relativa a un decesso"},
        {condition: isPaid, message: "La pratica è pagata"},
        {condition: isDaRendicontare, message: "La pratica è da rendicontare"},
        {condition: isRendicontata, message: "La pratica è rendicontata"},
        {condition: isTransferred, message: "La pratica è trasferita"},
        {condition: !hasProtocol, message: "La pratica non è protocollata"},
        {condition: !isNotified, message: "La pratica non è notificata"},
    ];

    const installmentsActiveReasons = installmentsReasons.filter(r => r.condition).map(r => r.message);
    const installmentsDisabled = installmentsActiveReasons.length > 0;

    const callLogSendModulesReasons: { condition: boolean; message: string }[] = [
        {condition: isDeath, message: "La pratica è relativa a un decesso"}
    ];

    const callLogSendModulesActiveReasons = callLogSendModulesReasons.filter(r => r.condition).map(r => r.message);
    const callLogSendModulesDisabled = callLogSendModulesActiveReasons.length > 0;

    const archiveReasons: { condition: boolean; message: string }[] = [
        {condition: !isSuspended, message: "La pratica non è sospesa"},
        {condition: isDeath, message: "La pratica è relativa a un decesso"},
    ];

    const archiveActiveReasons = archiveReasons.filter(r => r.condition).map(r => r.message);
    const archiveDisabled = archiveActiveReasons.length > 0;

    const suspendReasons: { condition: boolean; message: string }[] = [
        {condition: isArchived, message: "La pratica è archiviata"},
        {condition: isDaRendicontare, message: "La pratica è da rendicontare"},
        {condition: isRendicontata, message: "La pratica è rendicontata"},
        {condition: isTransferred, message: "La pratica è trasferita"},
        {condition: isDeath, message: "La pratica è relativa a un decesso"}
    ];

    const suspendActiveReasons = suspendReasons.filter(r => r.condition).map(r => r.message);

    const dropdownPlusItems = [
        {
            key: "suspendDirect",
            label: <span className={"suspend-label"}>Sospendi</span>
        },
        {
            key: "heir",
            label: !isSuspended || (suspensionReason !== SUSPENSION_REASON_LABELS.DECESSO && suspensionReason !== SUSPENSION_REASON_LABELS.SCADUTA_DECESSO) ? (
                <Tooltip title="Operazione consentita solo su pratiche sospese per decesso">
                    Deceduto con eredi
                </Tooltip>
            ) : (
                "Deceduto con eredi"
            ),
            disabled: !isSuspended || (suspensionReason !== SUSPENSION_REASON_LABELS.DECESSO && suspensionReason !== SUSPENSION_REASON_LABELS.SCADUTA_DECESSO)
        },
        {
            key: "recalculation",
            label: (() => {
                const baseCondition = isSuspended && suspensionReason === SUSPENSION_REASON_LABELS.ISTRUTTORIA;
                const eredeBlocked = eredeRicalcoloInfo?.isErede && !eredeRicalcoloInfo?.eligible;
                if (!baseCondition) {
                    return <Tooltip title="Operazione consentita solo su pratiche sospese per istruttoria">Ricalcolo importo dovuto</Tooltip>;
                }
                if (eredeBlocked) {
                    return <Tooltip title="Ricalcolo non consentito: il deceduto ha più di un erede o ha già effettuato pagamenti">Ricalcolo importo dovuto</Tooltip>;
                }
                return "Ricalcolo importo dovuto";
            })(),
            disabled: !isSuspended || suspensionReason !== SUSPENSION_REASON_LABELS.ISTRUTTORIA || (eredeRicalcoloInfo?.isErede === true && !eredeRicalcoloInfo?.eligible),
        },
        {
            key: "paymentConfirmed",
            label: !isSuspended || suspensionReason !== SUSPENSION_REASON_LABELS.ISTRUTTORIA ? (
                <Tooltip title="Operazione consentita solo su pratiche sospese per istruttoria">
                    Conferma pagamento
                </Tooltip>
            ) : (
                "Conferma pagamento"
            ),
            disabled: !isSuspended || suspensionReason !== SUSPENSION_REASON_LABELS.ISTRUTTORIA ,
        },
        {
            key: "missedPayment",
            label: !hasInstallments || !isSuspended ? (
                <Tooltip title="Operazione consentita solo su pratiche sospese con rate">
                    Mancato pagamento
                </Tooltip>
            ) : (
                "Mancato pagamento"
            ),
            disabled: !hasInstallments || readableToEnumStatus[practice.status] !== StatusCodeE.SOSPESE,
        },
    ];

    const reloadByIun = async (s:SendData) => {
        if(s.iun==null) return;
        const res =  await HttpService.getInstanceAxios().get(`/send/refresh/${s.iun}`)
        if(res.status===200){
            await fetchListOfSend();
        }
    }

    return (
        <>
            <Space className={"space-flex"} size={"middle"} direction={"vertical"}>
                <div className={"back-button"}>
                    <Button type="primary" onClick={() => onBack?.()}>
                        ← Indietro
                    </Button>
                </div>
                <Title level={4}>Dettaglio atto iniziale</Title>
                <Table
                    columns={columns}
                    className={"auto"}
                    pagination={false}
                    rowKey={(record) => record.protocolNumber}
                    dataSource={[practice]}
                />
                <div
                    onClick={() => setIsRateExpanded(!isRateExpanded)}
                    className={"expand-icon"}
                >
                    <Title level={4} style={{margin: 0}}>Dettaglio rate</Title>
                    {isRateExpanded ? (
                        <UpOutlined className={"expand-icon-size"}/>
                    ) : (
                        <DownOutlined className={"expand-icon-size"}/>
                    )}
                </div>
                {isRateExpanded && (
                    <Form form={installmentForm} component={false}>
                        <Table
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}
                            columns={mergedColumns}
                            className={"auto"}
                            pagination={false}
                            rowKey={"id"}
                            dataSource={listOfInstallments}
                        />
                    </Form>
                )}

                <div style={{display:"flex"}}>
                    <Title level={4}>Stato pratica</Title>
                    <Button style={{marginTop:"25px",marginLeft:"10px"}}
                        type="primary"
                        icon={<DownloadOutlined/>}
                        onClick={exportAuditExcel}
                    >
                        Esporta
                    </Button>
                </div>
                <PracticeDetailPanelSteps
                    status={practice.status}
                    communicationDate={practice.communicationDate}
                    auditTrail={auditTrail}
                />

                <div
                    onClick={() => setIsSendExpanded(!isSendExpanded)}
                    className={"expand-icon"}
                >
                    <Title level={4} style={{margin: 0}}>SEND</Title>
                    {isSendExpanded ? (
                        <UpOutlined className={"expand-icon-size"}/>
                    ) : (
                        <DownOutlined className={"expand-icon-size"}/>
                    )}
                </div>
                {isSendExpanded && (
                    <Form form={installmentForm} component={false}>
                        <Table
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}
                            columns={columnsSend}
                            className={"auto"}
                            pagination={false}
                            rowKey={"notificationRequestId"}
                            dataSource={listOfSend}
                        />
                    </Form>
                )}



                <Button
                    type="primary"
                    icon={<EyeOutlined/>}
                    loading={loading}
                    onClick={async () => {
                        if (simulatorId !== undefined && person?.id !== undefined) {
                            setLoading(true);
                            try {
                                const blob = await TemplateUtils.fetchGlyphedOrFallbackPDFBlob(simulatorId, person?.id);
                                if (blob) {
                                    const url = URL.createObjectURL(blob);
                                    setPdfUrl(url);
                                } else {
                                    message.error("Impossibile caricare il PDF.");
                                }
                            } finally {
                                setLoading(false);
                            }
                        }
                    }}
                >
                    Anteprima
                </Button>
                {pdfUrl && (
                    <div>
                        <div className={"preview-close-div"}>
                            <Button
                                type="default"
                                size="small"
                                onClick={() => setPdfUrl(null)}
                                icon={<CloseOutlined/>}
                                className={"preview-close"}
                            >
                                Chiudi anteprima
                            </Button>
                        </div>
                        <iframe
                            src={pdfUrl}
                            width="100%"
                            height="600px"
                        />
                    </div>
                )}
                <div
                    onClick={() => setIsCallLogExpanded(!isCallLogExpanded)}
                    className={"expand-icon"}
                >

                    <Title level={4} style={{margin: 0}}>Registro chiamate</Title>
                    {isCallLogExpanded ? (
                        <UpOutlined className={"expand-icon-size"}/>
                    ) : (
                        <DownOutlined className={"expand-icon-size"}/>
                    )}
                </div>

                {isCallLogExpanded && (
                    <List
                        size="small"
                        bordered
                        itemLayout="horizontal"
                        dataSource={listOfCalls}
                        renderItem={(item) => {
                            return (
                                <List.Item
                                    actions={[
                                        <Button
                                            type={item.isDone ? "primary" : "default"}
                                            onClick={() => {
                                                setChangeActivityModal(true);
                                                if (item.id !== undefined) {
                                                    setCallId(item.id);
                                                }
                                            }}

                                        >
                                            {item.isDone ? "Evasa" : "Da evadere"}
                                        </Button>
                                    ]}

                                >
                                    <List.Item.Meta
                                        key={item.id}
                                        title={`${item.basicUserDTO?.name + " " + item.basicUserDTO?.lastname}, ${UtilsService.formatDateHour(item.createdAt !== undefined ? item.createdAt : null)} (${item.isDone ? "Evasa" : "Da evadere"})`}
                                        description={
                                            <>
                                                <div>
                                                    <span className={"label-bold"}>Denominazione:</span>{" "}
                                                    <span
                                                        className="value-text"> {item.metadata.fullName ? item.metadata.fullName : "-"}</span>
                                                </div>
                                                <div>
                                                    <span className={"label-bold"}>Telefono:</span>{" "}
                                                    <span
                                                        className="value-text"> {item.metadata.phone ? item.metadata.phone : "-"}</span>
                                                </div>
                                                <div>
                                                    <span className={"label-bold"}>Email:</span>{" "}
                                                    <span
                                                        className="value-text"> {item.metadata.email ? item.metadata.email : "-"}</span>
                                                </div>
                                                <div>
                                                    <span className={"label-bold"}>Note chiamata:</span>{" "}
                                                    <span className="value-text"> {item.note}</span>
                                                </div>
                                            </>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />)}

                <div className={"button-div-max"}>
                    <div className={"button-div"}>

                        <Tooltip title={callLogSendModulesActiveReasons.join(" • ")}>
                            <Button className={"custom-button"} size="large" type={"primary"} danger={isCall}
                                    icon={<PhoneOutlined/>}
                                    onClick={() => setIsCall(!isCall)}
                                    disabled={callLogSendModulesDisabled}
                            >
                                Chiamata
                            </Button>
                        </Tooltip>

                        <Tooltip title={callLogSendModulesActiveReasons.join(" • ")}>
                            <Button className={"custom-button"} style={{marginBottom: 40}} size="large" type="primary"
                                    icon={<MailOutlined/>}
                                    onClick={() => setOpenModalKey("modules")}
                                    disabled={callLogSendModulesDisabled}
                            >
                                Invia moduli
                            </Button>
                        </Tooltip>

                        <Tooltip title={installmentsActiveReasons.join(" • ")}>
                            <Button
                                disabled={installmentsDisabled}
                                className={"custom-button"}
                                size="large"
                                type="primary"
                                icon={<OrderedListOutlined/>}
                                onClick={() => setOpenModalKey("installments")}
                            >
                                Rateizza
                            </Button>
                        </Tooltip>

                        <Tooltip title={archiveActiveReasons.join(" • ")}>
                            <Button
                                className={"custom-button"}
                                size="large"
                                type="primary"
                                icon={<InboxOutlined/>}
                                onClick={() => setOpenModalKey("archive")}
                                disabled={archiveDisabled}
                            >
                                Archivia</Button>
                        </Tooltip>

                        <Dropdown menu={{items: dropdownPlusItems, onClick: handlePlusMenu}} trigger={['click']}>
                            <Tooltip title={suspendActiveReasons.join(" • ")}>
                                <Button
                                    className={"custom-button"}
                                    size="large"
                                    danger
                                    icon={<ExclamationCircleFilled/>}
                                    disabled={false}
                                >
                                    Sospendi
                                </Button>
                            </Tooltip>
                        </Dropdown>
                    </div>

                    {isCall && (
                        <div className={"call-box-div"}>
                            <Form layout="vertical" form={form} name="call_form">
                                <div className={"call-box"}>
                                    <Form.Item label="Denominazione" name="name" className={"flex1"}>
                                        <Input placeholder="Inserisci denominazione"/>
                                    </Form.Item>
                                    <Form.Item
                                        label="Telefono"
                                        name="phone"
                                        className={"flex1"}
                                        rules={[
                                            {
                                                pattern: /^\d{8,11}$/,
                                                message: "Il numero di telefono deve contenere tra 8 e 11 cifre.",
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Inserisci numero" maxLength={11}/>
                                    </Form.Item>
                                    <Form.Item
                                        label="Email"
                                        name="email"
                                        rules={[{type: "email", message: "Inserisci un'email valida"}]}
                                        className={"flex1"}
                                    >
                                        <Input placeholder="Inserisci email"/>
                                    </Form.Item>
                                    <Form.Item
                                        label={" "}
                                        className={"flex1"}>
                                        <Button
                                            onClick={() => setUpdateContactsModal(true)}
                                            type={"primary"}>
                                            Aggiorna contatti
                                        </Button>
                                    </Form.Item>
                                </div>

                                <Form.Item
                                    label="Note"
                                    name="note"
                                    rules={[
                                        {required: true, message: "Inserire la nota del messaggio"},
                                    ]}
                                >
                                    <Input.TextArea rows={4} placeholder="Note chiamata"/>
                                </Form.Item>
                                <Form.Item>
                                    <div className={"call-box"}>
                                        <Button loading={isSubmittingCall} disabled={isSubmittingCall} onClick={() => handleSubmit(true)}>Evasa</Button>
                                        <Button loading={isSubmittingCall} disabled={isSubmittingCall} onClick={() => handleSubmit(false)}>Da evadere</Button>
                                    </div>
                                </Form.Item>
                            </Form>
                        </div>

                    )}
                </div>
            </Space>
            {openModalKey && modalComponents[openModalKey]}
            <ConfirmModal
                visible={updateContactModal}
                onConfirm={updateContactFromCall}
                onCancel={() => setUpdateContactsModal(false)}
                title={"Aggiorna contatti"}
                message={"Sei sicuro di voler aggiornare i contatti?"}
                confirmText={"Sì, aggiorna"}
                cancelText={"Annulla"}
            />
            <ConfirmModal
                visible={changeActivityModal}
                onConfirm={changeActivity}
                onCancel={() => setChangeActivityModal(false)}
                title={"Cambia stato chiamata"}
                message={"Sei sicuro di voler cambiare lo stato della chiamata?"}
                confirmText={"Sì, cambia"}
                cancelText={"Annulla"}
                loading={isChangingActivity}
            />
            <ConfirmModal
                visible={pendingLastSave !=null}
                onConfirm={async () => {
                    if (!pendingLastSave) return;
                    const { record, payload } = pendingLastSave;
                    setPendingLastSave(null);
                    await doSave(record, payload);

                    await refreshPractices?.()
                }}
                onCancel={() => setPendingLastSave(null)}
                title={"Conferma pagamento finale"}
                message={
                    pendingLastSave?.record.uniquePayment
                        ? "Stai per registrare il pagamento della rata unica. Lo stato della pratica sarà impostato su PAGATA. Vuoi procedere?"
                        : "Stai per registrare il pagamento dell’ultima rata. Lo stato della pratica sarà impostato su PAGATA. Vuoi procedere?"
                }
                confirmText={"Sì, continua"}
                cancelText={"Annulla"}
            />
        </>
    );
}
